/**
 * Cart Offer Ladder.
 *
 * Renders NOTHING inline on the page. It's a hidden modal that this script
 * opens as a centered popup on desktop (a bottom sheet on mobile), showing
 * a numbered milestone stepper, tier/perk details, and a free-sample
 * picker carousel for whichever tier the cart has currently reached.
 *
 * The modal opens ONLY after an "Add to cart" click — detected by patching
 * window.fetch to watch for cart-mutating requests (add/change/update/
 * clear), so it works with the theme's existing add-to-cart buttons with
 * no changes to them. It does NOT open just from clicking the cart icon —
 * that's intentional, so it doesn't collide with other apps (e.g. cart
 * upsell drawers) that also react to the cart icon.
 *
 * No discounts or gifts are actually applied here — thresholds, %s, gifts,
 * and benefit text are just labels configured in the theme editor. Actual
 * discounts/free shipping should be set up as native Shopify automatic
 * discounts; this component only communicates progress toward them and lets
 * customers claim their free sample product(s).
 */
(function () {
  'use strict';

  var CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  var SAMPLE_PROPERTY = '_offer_ladder_tier';

  var instance = null;
  var autoHideTimer = null;
  var lastKnownTotal = null;
  // Left undefined (not null) on purpose — "undefined" means "we haven't
  // observed a tier yet" (first load), while "null" is a real, meaningful
  // value (cart is below every threshold). Only compare once a real prior
  // observation exists, so the very first refresh never counts as a
  // "tier changed" event.
  var lastKnownTierThreshold;
  var lastFocusedBeforeOpen = null;

  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return el.offsetParent !== null; // skip hidden/display:none elements
    });
  }

  function trapFocus(evt) {
    if (!instance || evt.key !== 'Tab') return;
    var focusable = getFocusable(instance.root);
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  function formatRupees(amount) {
    return '₹' + Math.round(amount).toLocaleString('en-IN');
  }

  function getTiersFromDom(root) {
    return Array.from(root.querySelectorAll('[data-offer-ladder-step]'))
      .map(function (el) {
        return {
          el: el,
          threshold: parseFloat(el.getAttribute('data-threshold')) || 0
        };
      })
      .sort(function (a, b) { return a.threshold - b.threshold; });
  }

  function computeProgress(cartTotalRupees, tiers) {
    var reached = null;
    var next = null;

    for (var i = 0; i < tiers.length; i++) {
      if (cartTotalRupees >= tiers[i].threshold) {
        reached = tiers[i];
      } else {
        next = tiers[i];
        break;
      }
    }

    return { reached: reached, next: next };
  }

  /* ---------------------------------------------------------------------
   * Free-sample picker
   * ------------------------------------------------------------------- */

  function removeLineItem(key) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).catch(function () {});
  }

  function initPicker(picker) {
    if (picker.dataset.pickerInit === 'true') return;
    picker.dataset.pickerInit = 'true';

    var maxSamples = parseInt(picker.getAttribute('data-max-samples'), 10) || 0;
    var tierThreshold = parseFloat(picker.getAttribute('data-tier-threshold')) || 0;
    // Cards are gathered across ALL panels (Samples + Wellness Kit, when the
    // latter exists) — there is one combined selection count/cap per tier,
    // not one per tab, matching the imported design's shared counter.
    var cards = Array.from(picker.querySelectorAll('[data-offer-ladder-picker-card]'));
    var counters = Array.from(picker.querySelectorAll('[data-offer-ladder-picker-selected], [data-offer-ladder-picker-selected-btn]'));
    var submitBtn = picker.querySelector('[data-offer-ladder-picker-submit]');
    var feedbackEl = picker.querySelector('[data-offer-ladder-picker-feedback]');

    // Each panel (Samples, and Wellness Kit if present) gets its own
    // independent carousel — its own track, prev/next buttons, and dots —
    // scoped to just the cards inside that one panel.
    var panels = Array.from(picker.querySelectorAll('[data-offer-ladder-picker-panel]'));
    panels.forEach(function (panel) {
      var track = panel.querySelector('[data-offer-ladder-picker-track]');
      var panelCards = Array.from(panel.querySelectorAll('[data-offer-ladder-picker-card]'));
      var prevBtn = panel.querySelector('[data-offer-ladder-picker-prev]');
      var nextBtn = panel.querySelector('[data-offer-ladder-picker-next]');
      var dotsWrap = panel.querySelector('[data-offer-ladder-picker-dots]');

      if (dotsWrap) {
        panelCards.forEach(function (card, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'offer-ladder__picker-dot';
          dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
          dot.addEventListener('click', function () {
            card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
          dotsWrap.appendChild(dot);
        });
      }

      function updateDots() {
        if (!dotsWrap || !track) return;
        var dots = Array.from(dotsWrap.children);
        var trackLeft = track.getBoundingClientRect().left;
        var closestIndex = 0;
        var closestDist = Infinity;
        panelCards.forEach(function (card, i) {
          var dist = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closestIndex = i;
          }
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('offer-ladder__picker-dot--active', i === closestIndex);
        });
      }

      if (track) {
        track.addEventListener('scroll', function () {
          window.requestAnimationFrame(updateDots);
        });
        updateDots();
      }

      if (prevBtn && track) {
        prevBtn.addEventListener('click', function () {
          track.scrollBy({ left: -220, behavior: 'smooth' });
        });
      }
      if (nextBtn && track) {
        nextBtn.addEventListener('click', function () {
          track.scrollBy({ left: 220, behavior: 'smooth' });
        });
      }
    });

    // Tab switcher (Samples / Wellness Kit) — only present when a Wellness
    // Kit pool was configured. Switching tabs only changes which panel is
    // visible; the selection count/cap stays shared across both (computed
    // from the picker-wide `cards` list above), so choosing 2 in Samples and
    // 1 in Kit still counts as 3 total against the tier's limit.
    var tabs = Array.from(picker.querySelectorAll('[data-offer-ladder-picker-tab]'));
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-offer-ladder-picker-tab');
        tabs.forEach(function (t) {
          var isActive = t === tab;
          t.classList.toggle('offer-ladder__picker-tab--active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          var isActive = panel.getAttribute('data-offer-ladder-picker-panel') === target;
          panel.hidden = !isActive;
          panel.classList.toggle('offer-ladder__picker-panel--active', isActive);
        });
      });
    });

    function refreshCounters() {
      var selectedCards = cards.filter(function (c) {
        return c.classList.contains('offer-ladder__picker-card--selected');
      });
      var count = selectedCards.length;

      counters.forEach(function (el) { el.textContent = String(count); });
      if (submitBtn) submitBtn.disabled = count === 0;

      var atLimit = count >= maxSamples;
      cards.forEach(function (c) {
        var isSelected = c.classList.contains('offer-ladder__picker-card--selected');
        var btn = c.querySelector('[data-offer-ladder-picker-choose]');
        if (btn) btn.disabled = !isSelected && atLimit;
      });
    }

    cards.forEach(function (card) {
      var chooseBtn = card.querySelector('[data-offer-ladder-picker-choose]');
      if (!chooseBtn) return;

      chooseBtn.addEventListener('click', function () {
        var isSelected = card.classList.contains('offer-ladder__picker-card--selected');
        var currentCount = cards.filter(function (c) {
          return c.classList.contains('offer-ladder__picker-card--selected');
        }).length;

        if (isSelected) {
          card.classList.remove('offer-ladder__picker-card--selected');
          chooseBtn.textContent = 'Choose Sample';
          chooseBtn.setAttribute('aria-pressed', 'false');
        } else {
          if (currentCount >= maxSamples) return;
          card.classList.add('offer-ladder__picker-card--selected');
          chooseBtn.textContent = 'Selected';
          chooseBtn.setAttribute('aria-pressed', 'true');
        }
        refreshCounters();
      });
    });

    if (submitBtn) {
      // Move the button's existing markup (which includes the live counter
      // <span data-offer-ladder-picker-selected-btn>) into a permanent
      // wrapper, and toggle that wrapper's visibility against a separate
      // "Adding…" node instead of ever overwriting the button's innerHTML.
      // The old code did `submitBtn.textContent = 'Adding…'` then restored
      // `submitBtn.innerHTML = originalLabel` afterward — textContent wipes
      // all child nodes (including the counter span), and restoring via
      // innerHTML recreates a brand-new, disconnected span, so the counter
      // this component reads from (`counters`, captured once at init) goes
      // stale and the "(X/Y)" label freezes after the first submit. Keeping
      // the original span alive and just hiding/showing it fixes that.
      var readyWrap = document.createElement('span');
      readyWrap.setAttribute('data-offer-ladder-picker-submit-ready', '');
      while (submitBtn.firstChild) readyWrap.appendChild(submitBtn.firstChild);
      submitBtn.appendChild(readyWrap);

      var loadingWrap = document.createElement('span');
      loadingWrap.setAttribute('data-offer-ladder-picker-submit-loading', '');
      loadingWrap.hidden = true;
      loadingWrap.textContent = 'Adding…';
      submitBtn.appendChild(loadingWrap);

      submitBtn.addEventListener('click', function () {
        var selectedCards = cards.filter(function (c) {
          return c.classList.contains('offer-ladder__picker-card--selected');
        });
        if (selectedCards.length === 0) return;

        submitBtn.disabled = true;
        readyWrap.hidden = true;
        loadingWrap.hidden = false;

        // Hard cap, independent of whatever the UI's own selection-time
        // guard already did: never send more line items than this tier's
        // configured limit, no matter how they were selected.
        var cappedCards = selectedCards.slice(0, maxSamples);
        var items = cappedCards.map(function (c) {
          var btn = c.querySelector('[data-offer-ladder-picker-choose]');
          var properties = {};
          properties[SAMPLE_PROPERTY] = String(tierThreshold);
          return {
            id: parseInt(btn.getAttribute('data-variant-id'), 10),
            quantity: 1,
            properties: properties
          };
        });

        // If the customer already claimed sample(s) at THIS tier earlier
        // (e.g. the popup re-opened after another add-to-cart while they're
        // still sitting between two thresholds) and now submits a
        // different pick, remove the earlier claim first so the new
        // selection REPLACES it instead of stacking on top — otherwise
        // re-submitting could push a tier past its configured sample limit.
        // If they don't touch the picker at all this time, this code never
        // runs (only a submit click gets here), so an untouched earlier
        // claim is left exactly as it was.
        //
        // cache: 'no-store' on this read matters — without it the browser
        // can serve a STALE cached /cart.js response (e.g. one fetched a
        // moment earlier by the page's own cart-refresh logic) that's
        // missing an item added just before this click, which would let an
        // old claim slip past this removal step and let the tier's total
        // creep past its configured max.
        fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            var existingForTier = (cart.items || []).filter(function (item) {
              return item.properties && item.properties[SAMPLE_PROPERTY] === String(tierThreshold);
            });
            return Promise.all(existingForTier.map(function (item) {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: 0 })
              });
            }));
          })
          .then(function () {
            if (items.length === 0) return { items: [] };
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: items })
            }).then(function (res) { return res.json(); });
          })
          .then(function () {
            if (feedbackEl) {
              feedbackEl.textContent = 'Free sample' + (cappedCards.length > 1 ? 's' : '') + ' added to your cart.';
            }
            cappedCards.forEach(function (c) {
              c.classList.remove('offer-ladder__picker-card--selected');
              var btn = c.querySelector('[data-offer-ladder-picker-choose]');
              if (btn) btn.textContent = 'Choose Sample';
            });
            refreshCounters();
            loadingWrap.hidden = true;
            readyWrap.hidden = false;
            refreshAll();
            // Close the popup once the sample is actually in the cart — the
            // customer's task here ("claim my free sample") is done, no
            // reason to make them close it themselves. Small delay so the
            // "added to your cart" feedback text above is actually readable
            // before the modal disappears, instead of vanishing instantly.
            window.setTimeout(closeModal, 900);
          })
          .catch(function () {
            if (feedbackEl) feedbackEl.textContent = 'Something went wrong. Please try again.';
            submitBtn.disabled = false;
            loadingWrap.hidden = true;
            readyWrap.hidden = false;
          });
      });
    }

    refreshCounters();
  }

  function updateAllPickers(cart, cartTotalRupees, progress) {
    var pickers = Array.from(document.querySelectorAll('[data-offer-ladder-picker]'));
    var reachedThreshold = progress.reached ? progress.reached.threshold : null;
    var anyVisible = false;

    pickers.forEach(function (picker) {
      var tierThreshold = parseFloat(picker.getAttribute('data-tier-threshold')) || 0;
      var isCurrentTier = reachedThreshold !== null && tierThreshold === reachedThreshold;
      picker.hidden = !isCurrentTier;
      if (isCurrentTier) anyVisible = true;
    });

    // Widen the modal panel so the picker's 4-across carousel fits without
    // the panel needing to scroll.
    if (instance) {
      var panel = instance.root.querySelector('.offer-ladder-modal__panel');
      if (panel) panel.classList.toggle('offer-ladder-modal__panel--wide', anyVisible);
    }

    // A sample is only removed when the cart has DEFINITELY progressed to a
    // DIFFERENT, higher tier — never just because this particular refresh
    // couldn't confirm a current tier (reachedThreshold === null happens on
    // every popup-triggering refresh for a moment, and also transiently
    // whenever this runs before the cart total has finished loading). That
    // used to read as "not still current" and wipe every existing sample on
    // practically every popup open/close, even when the shopper hadn't
    // touched the picker at all. Only a CONFIRMED different tier — not an
    // unknown one — invalidates an earlier claim; this still blocks the
    // original double-dip case (claim at tier 2, keep shopping to tier 4,
    // claim tier 4's allotment ALSO), since that always resolves to a
    // definite, different reachedThreshold.
    (cart.items || []).forEach(function (item) {
      if (!item.properties || !item.properties[SAMPLE_PROPERTY]) return;
      var tierThreshold = parseFloat(item.properties[SAMPLE_PROPERTY]);
      var confirmedDifferentTier = reachedThreshold !== null && tierThreshold !== reachedThreshold;
      if (confirmedDifferentTier) {
        removeLineItem(item.key);
        return;
      }
      // Safety net: a free sample line should never carry quantity > 1. The
      // submit flow already clears any earlier claim at the same tier
      // before adding a fresh one (see initPicker's submit handler), but
      // this catches it regardless of how a quantity of 2+ got here — e.g.
      // a merchant's own quantity-selector UI touching the same line, or
      // Shopify's /cart/add.js merging into an existing line because its
      // variant + properties matched exactly. A shopper should never end up
      // with 2 of a product that's meant to be a single free gift.
      if (item.quantity > 1) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 1 })
        }).catch(function () {});
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Modal (stepper + tier list + picker)
   * ------------------------------------------------------------------- */

  function openModal() {
    if (!instance) return;
    // Remember who had focus so it can be restored on close, and trap Tab
    // inside the panel while open — without this, a keyboard/screen-reader
    // user can tab straight through a role="dialog" aria-modal="true" into
    // the page behind it, which is a real modal-dialog a11y failure.
    lastFocusedBeforeOpen = document.activeElement;
    document.addEventListener('keydown', trapFocus, true);

    // Re-append as the very last node in <body>. Some apps (cart upsell
    // drawers, etc.) also use the maximum possible z-index; when z-index
    // is tied, the element that's LATER in paint/DOM order wins, so this
    // guarantees our modal stays on top of anything already in the page,
    // even things added after our script first ran.
    if (instance.root.parentElement !== document.body || document.body.lastElementChild !== instance.root) {
      document.body.appendChild(instance.root);
    }
    instance.root.hidden = false;
    // Force reflow so the CSS animation replays each time it reopens.
    void instance.root.offsetWidth;
    instance.root.classList.add('offer-ladder-modal--visible');
    document.documentElement.classList.add('offer-ladder-lock-scroll');

    var autoHideSeconds = parseFloat(instance.root.getAttribute('data-auto-hide')) || 0;
    var hasVisiblePicker = Array.from(instance.root.querySelectorAll('[data-offer-ladder-picker]')).some(function (p) {
      return !p.hidden;
    });

    if (autoHideTimer) window.clearTimeout(autoHideTimer);
    if (autoHideSeconds > 0 && !hasVisiblePicker) {
      autoHideTimer = window.setTimeout(closeModal, autoHideSeconds * 1000);
    }

    // Move focus into the panel so keyboard/screen-reader users land inside
    // the dialog rather than staying on whatever triggered it.
    var closeBtn = instance.root.querySelector('[data-offer-ladder-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!instance) return;
    instance.root.classList.remove('offer-ladder-modal--visible');
    document.documentElement.classList.remove('offer-ladder-lock-scroll');
    document.removeEventListener('keydown', trapFocus, true);
    if (autoHideTimer) window.clearTimeout(autoHideTimer);
    window.setTimeout(function () {
      if (!instance.root.classList.contains('offer-ladder-modal--visible')) {
        instance.root.hidden = true;
      }
    }, 300);

    // Restore focus to whatever had it before the modal opened (typically
    // the "Add to cart" button), instead of leaving focus stranded.
    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus();
    }
    lastFocusedBeforeOpen = null;
  }

  function updateInstance(cart) {
    var cartTotalRupees = (cart.total_price || 0) / 100;
    var tiers = getTiersFromDom(instance.root);
    var progress = computeProgress(cartTotalRupees, tiers);

    updateAllPickers(cart, cartTotalRupees, progress);

    // Numbered stepper — circle state. The highest configured tier gets a
    // distinct "--top" treatment once reached (see CSS), so the biggest
    // spender's moment reads as a peak, not identical to reaching tier 1.
    var topTier = tiers.length > 0 ? tiers[tiers.length - 1] : null;
    var topTierReached = !!(progress.reached && topTier && progress.reached.threshold === topTier.threshold);
    tiers.forEach(function (tier) {
      var isReached = cartTotalRupees >= tier.threshold;
      var isCurrent = !isReached && progress.next && progress.next.threshold === tier.threshold;
      var isTop = !!(topTier && tier.threshold === topTier.threshold);
      tier.el.classList.toggle('offer-ladder-modal__step--reached', isReached);
      tier.el.classList.toggle('offer-ladder-modal__step--current', !!isCurrent);
      tier.el.classList.toggle('offer-ladder-modal__step--top-reached', isReached && isTop);
    });
    instance.root.classList.toggle('offer-ladder-modal--top-tier', topTierReached);

    // Continuous progress fill: a lead segment from 0 -> first tier, then
    // one segment per gap between consecutive tiers. Each segment fills
    // proportionally to how far the cart is through that specific range,
    // so progress shows even before tier 1, and stops exactly mid-segment
    // when the cart value sits between two tiers.
    function segmentPct(lo, hi) {
      if (hi <= lo) return 100;
      return Math.max(0, Math.min(100, ((cartTotalRupees - lo) / (hi - lo)) * 100));
    }

    // Single-layer gradient fill: set the --ol-lead-pct / --ol-line-pct
    // custom property directly on the track element itself (not a separate
    // absolutely-positioned child), so there's exactly one painted layer
    // and no chance of a fill child's box getting out of sync with its
    // parent's box (which was the root cause of the "line is not full" /
    // gap-rendering bug reported on the live site).
    var lead = instance.root.querySelector('[data-offer-ladder-lead]');
    if (lead && tiers.length > 0) {
      lead.style.setProperty('--ol-lead-pct', segmentPct(0, tiers[0].threshold) + '%');
    }

    tiers.forEach(function (tier, i) {
      var line = tier.el.querySelector('[data-offer-ladder-step-line]');
      if (!line) return;
      var next = tiers[i + 1];
      var pct = next ? segmentPct(tier.threshold, next.threshold) : 0;
      line.style.setProperty('--ol-line-pct', pct + '%');
    });

    // Reward pills for the tier just reached (only one group visible at a time).
    Array.from(instance.root.querySelectorAll('[data-offer-ladder-perk-group]')).forEach(function (group) {
      var threshold = parseFloat(group.getAttribute('data-threshold')) || 0;
      group.hidden = !(progress.reached && progress.reached.threshold === threshold);
    });

    // Status line.
    var statusEl = instance.root.querySelector('[data-offer-ladder-status]');
    if (statusEl) {
      if (progress.next) {
        var remaining = progress.next.threshold - cartTotalRupees;
        statusEl.textContent = 'Add ' + formatRupees(remaining) + ' more to unlock your next reward';
      } else if (topTierReached) {
        statusEl.textContent = 'You’ve unlocked every reward — our best tier, just for you.';
      } else if (progress.reached) {
        statusEl.textContent = 'All rewards unlocked — nice!';
      } else {
        statusEl.textContent = 'Add items to see your rewards';
      }
    }

    return { cartTotalRupees: cartTotalRupees, progress: progress };
  }

  function refreshAll(openOnChange) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // An increase always opens the modal (add-to-cart, or a quantity
        // bump in the cart drawer). A DECREASE only opens it when the drop
        // actually crosses a threshold backwards — losing free delivery or
        // dropping a discount tier is worth surfacing — not for every
        // trivial quantity tweak that leaves the customer at the same tier.
        var totalIncreased = lastKnownTotal !== null && cart.total_price > lastKnownTotal;
        var totalDecreased = lastKnownTotal !== null && cart.total_price < lastKnownTotal;
        lastKnownTotal = cart.total_price;

        var result = updateInstance(cart);
        var newTierThreshold = result.progress.reached ? result.progress.reached.threshold : null;
        var tierChanged = typeof lastKnownTierThreshold !== 'undefined' && newTierThreshold !== lastKnownTierThreshold;
        lastKnownTierThreshold = newTierThreshold;

        if (openOnChange && (totalIncreased || (totalDecreased && tierChanged))) {
          openModal();
        }
      })
      .catch(function () {
        /* Cart fetch failed silently; modal just won't update this cycle. */
      });
  }

  function patchFetch() {
    if (window.__offerLadderFetchPatched) return;
    window.__offerLadderFetchPatched = true;

    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
      var isCartRequest = CART_ENDPOINTS.some(function (endpoint) {
        return urlStr.indexOf(endpoint) !== -1;
      });

      var result = originalFetch.apply(this, arguments);

      if (isCartRequest) {
        result.then(function () {
          // Every cart mutation (add, change/update quantity, clear)
          // reaches here — refreshAll's own totalIncreased check above is
          // what actually decides whether to pop the modal open, so a
          // quantity bump from the cart drawer's +/- controls opens it
          // just like an add-to-cart click, while a decrease or removal
          // only refreshes state.
          window.setTimeout(function () { refreshAll(true); }, 50);
        }).catch(function () {});
      }

      return result;
    };
  }

  function initInstance(root) {
    if (!root || root.dataset.offerLadderInit === 'true') return;
    root.dataset.offerLadderInit = 'true';
    instance = { root: root };

    var overlay = root.querySelector('[data-offer-ladder-overlay]');
    if (overlay) overlay.addEventListener('click', closeModal);

    var closeBtn = root.querySelector('[data-offer-ladder-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && root.classList.contains('offer-ladder-modal--visible')) {
        closeModal();
      }
    });
  }

  function initAll() {
    var root = document.querySelector('[data-offer-ladder]');
    initInstance(root);
    document.querySelectorAll('[data-offer-ladder-picker]').forEach(initPicker);

    patchFetch();
    refreshAll(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target.querySelector('[data-offer-ladder]');
    if (section) {
      section.dataset.offerLadderInit = 'false';
      initInstance(section);
    }
    document.querySelectorAll('[data-offer-ladder-picker]').forEach(initPicker);
    refreshAll(false);
  });
})();